/*
 * Copyright (c) Nexign, JSC, 1992-2022.
 */
/**
 * Personal data delete modal windows saga.
 *
 * @author Dmitriy Kvon
 * @version 1.17.0
 * @since 1.17.0
 */

import { createElement } from 'react';

import { SagaIterator } from 'redux-saga';
import { all, call, put, select, takeEvery } from 'redux-saga/effects';

import { makeSelectIsFeatureWithVersionEnabled } from '@/main/pages/PageAuth/selectors/authSelectors';
import { RoutePath } from '@/main/routes/constants/routesConstants';

import { requestSaga } from '../../sagas/requestSaga';
import { API_PERSONAL_DATA_DELETE } from '../app/Api';
import { appHistoryPush } from '../app/appHistory';
import { makeSelectTranslationCommon } from '../internationalization/intSelectors';
import { ICommonTranslation } from '../internationalization/types';
import { ModalMultipleOrders } from '../modal/components/ModalMultipleOrders';
import { modalOpen, modalSetStatusAction } from '../modal/modalActions';
import { EModalStatus } from '../modal/types/modalTypes';
import { FeatureNameEnum } from '../role/constants/guard/guardConstants';
import { personalDataDeleteSuccessAction } from './personalDataDeleteActions';
import { PERSONAL_DATA_DELETE_START } from './personalDataDeleteConstants';

export function* personalDataDeleteProcessingSaga(): SagaIterator {
    try {
        // draws loader
        yield put(modalSetStatusAction({ status: EModalStatus.processing }));

        // calls gate api
        const { orderId } = yield call(requestSaga, API_PERSONAL_DATA_DELETE, 'DELETE');

        const isMegaPowersFeatureGreaterOrEqualThan2 = yield select(
            makeSelectIsFeatureWithVersionEnabled(FeatureNameEnum.MEGA_POWERS, 2, true),
        );

        if (isMegaPowersFeatureGreaterOrEqualThan2) {
            const { orderCreationModal }: ICommonTranslation = yield select(makeSelectTranslationCommon());

            const goToSubscribes = () => appHistoryPush(RoutePath.subscriberMobile);
            const goToMain = () => appHistoryPush(RoutePath.main);

            yield put(
                modalOpen({
                    render: () =>
                        createElement(ModalMultipleOrders, {
                            isOpen: true,
                            orderOrOrders: orderId,
                            onClose: goToSubscribes,
                            onSuccessButtonClick: goToMain,
                            orderTypeTranlation: orderCreationModal.success.orderType.PERSONAL_DATA_DELETE,
                        }),
                }),
            );
        } else {
            yield put(personalDataDeleteSuccessAction({ orderId }));
        }

        // sets status success
        yield put(modalSetStatusAction({ status: EModalStatus.success }));
    } catch (e) {
        yield put(modalSetStatusAction({ status: EModalStatus.failure }));
    }
}

// events listener for actions
export function* personalDataDeleteSaga() {
    yield all([takeEvery(PERSONAL_DATA_DELETE_START, personalDataDeleteProcessingSaga)]);
}
